import {
  WSElement,
  WSFlexBox,
  WSList,
  WSLoader
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPersons } from "@query";
import { useSession } from "../../../v3/hooks/useSession";
import { DynamicFormPrereqGate } from "../../DynamicForm/components/DynamicFormPrereqGate";
import { DynamicFormRenderer } from "../../DynamicForm/components/DynamicFormRenderer";
import { useEmployeeTaxFormsEngineConfig } from "../config/useEmployeeTaxFormsEngineConfig";
import {
  routeHelpersEmployeeTaxFormsForm,
  routeHelpersEmployeeTaxFormsFormResult
} from "../paths";
import { useQueryEmployeeTaxFormSchema } from "../../../query/employeeTaxForms/queries/useQueryEmployeeTaxFormSchema";
import { useEmployeeTaxFormsPairFromFormId } from "../hooks/useEmployeeTaxFormsPairFromFormId";
import { useEmployeeTaxFormsDataAdapter } from "../hooks/useEmployeeTaxFormsDataAdapter";
import { selectorShortFormTitle } from "../selectors/selectorShortFormTitle";
import { ISubmitFormResponse } from "../types";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";

// Tax-forms binding of the engine's presentational prereq gate. Purely
// informational — no checklist lookup, no navigation: DynamicFormPrereqGate
// already degrades gracefully to plain-text rows (falling back to the
// catalog formId as the label) when no title/onOpen is supplied. The
// checklist row list already prevents navigating here for a blocked form in
// the normal flow (ChecklistRows renders blocked items as locked, not
// clickable) — this gate is the defensive fallback for a direct/stale URL.
const EmployeeTaxFormPrereqGate: React.FC<{
  blockedBy: string[];
  formTitle?: string;
}> = ({ blockedBy, formTitle }) => (
  <DynamicFormPrereqGate
    blockers={blockedBy.map(blockerFormId => ({ key: blockerFormId }))}
    formTitle={formTitle}
  />
);

export const EmployeeTaxFormRenderer: React.FC<FlowStep> = ({ onNext }) => {
  const {
    params: { formInstanceId },
    state
  } = routeHelpersEmployeeTaxFormsForm.useData();
  const history = useHistory();
  const goToResult = routeHelpersEmployeeTaxFormsFormResult.useGoTo();
  // v3: the signer's display name comes from the Person read — the person's
  // ComplianceEntity (their own KYC record) is always inlined, no expand.
  const { personId } = useSession();
  const queryPerson = queriesPersons.getPerson.useQuery({ personId });
  const querySchema = useQueryEmployeeTaxFormSchema(formInstanceId);
  const { payerAccountId, isLoading: isPairLoading } =
    useEmployeeTaxFormsPairFromFormId(formInstanceId);
  const engineConfig = useEmployeeTaxFormsEngineConfig();
  const dataAdapter = useEmployeeTaxFormsDataAdapter(formInstanceId);

  const signedByName = React.useMemo(() => {
    const person = queryPerson.data;
    const legal = person?.complianceEntity?.individualLegalName;
    return (
      legal?.fullLegalName ||
      [legal?.givenName, legal?.familyName].filter(Boolean).join(" ") ||
      person?.complianceEntity?.legalName ||
      person?.complianceEntity?.preferredName ||
      person?.email ||
      ""
    );
  }, [queryPerson.data]);

  // Gate on the schema and the pair lookup (needed for the draft-scope key) —
  // the signer-name read is display-only and must never block the form (it
  // settles in the background; the name recomputes).
  if (querySchema.isLoading || isPairLoading) {
    return (
      <WSFlexBox.Center py="3XL">
        <WSLoader.Spinner size="M" />
      </WSFlexBox.Center>
    );
  }

  if (querySchema.error || !querySchema.data) {
    return (
      <ErrorAlert
        context="EmployeeTaxFormSchema"
        error={querySchema.error ?? "Couldn't load form"}
      />
    );
  }

  const response = querySchema.data;

  if (response.blockedBy.length > 0) {
    return (
      <EmployeeTaxFormPrereqGate
        blockedBy={response.blockedBy}
        formTitle={response.title}
      />
    );
  }

  const handleSubmitSuccess = (_submitResponse: ISubmitFormResponse) => {
    // Launched from a requirement details page → return there. The inline
    // checklist refreshes (submit invalidates the pair-scoped list query) so
    // the contractor sees the updated status and opens the next form. This is
    // the only reachable path today (every real navigation into this route
    // sets backPath — see EmployeeTaxFormsRequirementChecklist/ChecklistItem).
    if (state.backPath) {
      history.push(state.backPath);
      return;
    }
    // Standalone/embedded onboarding-wizard variant (no backPath) — not
    // currently reachable from any in-app entry point, only a theoretical
    // embedded-SDK one (`?modules=employee_tax_forms`, no requirement
    // context). "Are more forms left" no longer comes back on the submit
    // response; this variant needs its own pair-scoped fix before it's live.
    goToResult({ formInstanceId });
    onNext?.();
  };

  return (
    <WSList gap="XL">
      <WSElement>
        <DynamicFormRenderer
          // Sections/RHF defaults are captured at mount; key by revision so a
          // stale-revision reload (refetched schema with a new version)
          // remounts the renderer instead of submitting old fields against the
          // new version.
          key={`${formInstanceId}:${response.version}`}
          adapter={dataAdapter}
          response={response}
          contextId={payerAccountId ?? ""}
          shortTitle={selectorShortFormTitle(response.title)}
          signedByName={signedByName}
          onSubmitSuccess={handleSubmitSuccess}
          engineConfig={engineConfig}
        />
      </WSElement>
    </WSList>
  );
};
